import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { adminApi, DashboardSummary } from '../../api/client';

const COLORS = {
  labels: '#4F46E5',
  errors: '#EF4444',
};

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const activityTypeLabels: Record<string, { label: string; color: string }> = {
  label_generated: { label: 'Label Generated', color: 'bg-blue-100 text-blue-800' },
  tenant_created: { label: 'Tenant Created', color: 'bg-green-100 text-green-800' },
  template_uploaded: { label: 'Template Uploaded', color: 'bg-purple-100 text-purple-800' },
};

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: summary, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'dashboard', refreshKey],
    queryFn: () => adminApi.getDashboardSummary(),
    refetchInterval: 60000,
  });

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-8 w-56 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-40 bg-gray-100 rounded animate-pulse mt-2" />
          </div>
          <div className="h-10 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-5 border-l-4 border-l-gray-200 space-y-3">
              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="h-7 w-16 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="h-[280px] bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-red-800">Failed to load dashboard</h2>
        <p className="text-red-600 mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const data = summary as DashboardSummary;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📊 Admin Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">System overview and quick actions</p>
        </div>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          ↻ Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard
          label="Tenants"
          value={formatNumber(data?.tenants.total ?? 0)}
          subtitle={`${data?.tenants.active ?? 0} active`}
          color="blue"
          onClick={() => navigate('/admin/tenants')}
        />
        <KpiCard
          label="Templates"
          value={formatNumber(data?.templates.total ?? 0)}
          subtitle={`${data?.templates.active ?? 0} active`}
          color="purple"
          onClick={() => navigate('/admin/templates')}
        />
        <KpiCard
          label="Labels This Month"
          value={formatNumber(data?.usage.thisMonth.totalLabels ?? 0)}
          subtitle={`${data?.usage.thisMonth.errorCount ?? 0} errors`}
          color="green"
        />
        <KpiCard
          label="Est. Revenue"
          value={formatCurrency(data?.usage.thisMonth.estimatedRevenue ?? 0)}
          subtitle="this month"
          color="amber"
          onClick={() => navigate('/admin/billing')}
        />
        <KpiCard
          label="System Health"
          value={
            <span className={`inline-flex items-center ${
              data?.health.status === 'healthy' ? 'text-green-700' :
              data?.health.status === 'degraded' ? 'text-yellow-700' :
              'text-red-700'
            }`}>
              <span className={`w-2.5 h-2.5 rounded-full mr-2 ${
                data?.health.status === 'healthy' ? 'bg-green-500' :
                data?.health.status === 'degraded' ? 'bg-yellow-500' :
                'bg-red-500'
              }`} />
              {(data?.health.status ?? 'unknown').charAt(0).toUpperCase() + (data?.health.status ?? 'unknown').slice(1)}
            </span>
          }
          subtitle={`${data?.health.storageLatencyMs ?? 0}ms storage`}
          color="gray"
          onClick={() => navigate('/admin/monitoring')}
        />
      </div>

      {/* Chart + Quick Actions row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 7-Day Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">7-Day Label Trend</h2>
          {data?.usage.trend && data.usage.trend.some(d => d.labels > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.usage.trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(val: string) => {
                    const d = new Date(val);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                  fontSize={12}
                />
                <YAxis fontSize={12} />
                <Tooltip
                  labelFormatter={(val: string) => new Date(val).toLocaleDateString()}
                />
                <Area
                  type="monotone"
                  dataKey="labels"
                  stroke={COLORS.labels}
                  fill={COLORS.labels}
                  fillOpacity={0.15}
                  name="Labels"
                />
                <Area
                  type="monotone"
                  dataKey="errors"
                  stroke={COLORS.errors}
                  fill={COLORS.errors}
                  fillOpacity={0.15}
                  name="Errors"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-gray-400">
              <div className="text-center">
                <p className="text-4xl mb-2">📈</p>
                <p>No label activity in the past 7 days</p>
                <p className="text-sm mt-1">Generate some labels to see trends here</p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <QuickAction
              icon="👥"
              label="Create Tenant"
              description="Set up a new client organization"
              onClick={() => navigate('/admin/tenants')}
            />
            <QuickAction
              icon="📄"
              label="Upload Template"
              description="Add a new ZPL label template"
              onClick={() => navigate('/admin/templates')}
            />
            <QuickAction
              icon="🔍"
              label="View Health"
              description="Check system status and metrics"
              onClick={() => navigate('/admin/monitoring')}
            />
            <QuickAction
              icon="💰"
              label="Billing Export"
              description="Download monthly billing report"
              onClick={() => navigate('/admin/billing')}
            />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        {data?.recentActivity && data.recentActivity.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.recentActivity.map((event, idx) => {
                  const typeInfo = activityTypeLabels[event.type] || { label: event.type, color: 'bg-gray-100 text-gray-800' };
                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {timeAgo(event.timestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {event.details}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p className="text-4xl mb-2">📋</p>
            <p>No recent activity</p>
            <p className="text-sm mt-1">Activity will appear here as tenants and templates are created</p>
          </div>
        )}
      </div>

      {/* Deployment Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">🚀 Deployment Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">API Version</dt>
            <dd className="mt-1 text-sm font-mono text-gray-900">v1</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Functions Registered</dt>
            <dd className="mt-1 text-sm text-gray-900">{data?.health.status === 'healthy' ? '37' : '--'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Environment</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {window.location.hostname.includes('localhost') ? 'Local Development' :
               window.location.hostname.includes('purple-desert') ? 'Development' : 'Production'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Storage Latency</dt>
            <dd className="mt-1 text-sm text-gray-900">{data?.health.storageLatencyMs ?? '--'}ms</dd>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Sub-components ---

function KpiCard({
  label,
  value,
  subtitle,
  color,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  subtitle: string;
  color: string;
  onClick?: () => void;
}) {
  const colorMap: Record<string, string> = {
    blue: 'border-l-blue-500',
    purple: 'border-l-purple-500',
    green: 'border-l-green-500',
    amber: 'border-l-amber-500',
    gray: 'border-l-gray-500',
  };

  return (
    <div
      className={`bg-white rounded-lg shadow p-5 border-l-4 ${colorMap[color] || 'border-l-gray-500'} ${
        onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
      }`}
      onClick={onClick}
    >
      <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
      <dd className="mt-1 text-2xl font-bold text-gray-900">{value}</dd>
      <dd className="text-xs text-gray-400 mt-1">{subtitle}</dd>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  description,
  onClick,
}: {
  icon: string;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
    >
      <span className="text-xl">{icon}</span>
      <div>
        <div className="text-sm font-medium text-gray-900">{label}</div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
    </button>
  );
}
